/**
 * Shared pieces of the sign-up and onboarding screens (screenshots/93–105): the cream form column + plum stage, a
 * labelled field whose error sits next to the input (never inside the <label>, so screen readers read the label once
 * and the error as the input's description), and the 6-box code input (104).
 */
import { useRef, type ReactNode, type Ref } from "react";

export const LOGO = "/dashboard/brand/chicago-politics.png";
/** The stage: a still of the design's ribbed-glass shader (tile-gradient.glsl) in the brand plums. */
const STAGE = { "--stage-image": "url(/dashboard/brand/civic-stage.webp)" } as React.CSSProperties;

export function AuthLayout({ top, children, stage, className = "" }: { top?: ReactNode; children: ReactNode; stage: ReactNode; className?: string }) {
  return (
    <div className={`auth ${className}`}>
      <main className="auth__form">
        <header className="auth__top">
          <img src={LOGO} alt="Chicago Politics" width={150} height={60} />
          {top}
        </header>
        {children}
      </main>
      <aside className="auth__stage" aria-label="Your Civic Pass" style={STAGE}>{stage}</aside>
    </div>
  );
}

/** Label, input, then the error or note as the input's description (aria-describedby). */
export function AuthField({ id, label, error, note, trailing, inputRef, ...input }: {
  id: string; label: string; error?: string; note?: string; trailing?: ReactNode; inputRef?: Ref<HTMLInputElement>;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  const described = error ? `${id}-err` : note ? `${id}-note` : undefined;
  return (
    <div className="auth__field">
      <label htmlFor={id}>{label}</label>
      <span className={`auth__input${trailing ? " has-trailing" : ""}`}>
        <input id={id} ref={inputRef} aria-invalid={error ? true : undefined} aria-describedby={described} {...input} />
        {trailing}
      </span>
      {error ? <small id={`${id}-err`} className="auth__error">{error}</small>
        : note ? <small id={`${id}-note`} className="auth__ok">{note}</small> : null}
    </div>
  );
}

/** Six one-digit boxes (104). Typing moves forward, Backspace moves back, a pasted code fills every box. */
export function CodeBoxes({ value, onChange, label, autoFocus }: { value: string; onChange: (code: string) => void; label: string; autoFocus?: boolean }) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const set = (code: string) => onChange(code.replace(/\D/g, "").slice(0, 6));
  return (
    <div className="code-boxes" role="group" aria-label={label}>
      {Array.from({ length: 6 }, (_, i) => (
        <input key={i} ref={(el) => { refs.current[i] = el; }} inputMode="numeric" autoComplete={i === 0 ? "one-time-code" : "off"} maxLength={6}
          aria-label={`Digit ${i + 1}`} value={value[i] ?? ""} autoFocus={autoFocus && i === Math.min(value.length, 5)}
          onChange={(e) => {
            const d = e.target.value.replace(/\D/g, "");
            if (d.length > 1) { set(d); refs.current[Math.min(d.length, 5)]?.focus(); return; }   // pasted or autofilled
            set(value.slice(0, i) + d + value.slice(i + 1));
            if (d) refs.current[i + 1]?.focus();
          }}
          onKeyDown={(e) => { if (e.key === "Backspace" && !value[i] && i > 0) { set(value.slice(0, i - 1)); refs.current[i - 1]?.focus(); } }} />
      ))}
    </div>
  );
}
